import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PartnerAdminDashboard from "./PartnerAdminDashboard";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import {
  Briefcase,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
  TrendingUp,
  Clock,
  Star,
  Activity,
  ChevronRight,
  Sparkles,
  X as XIcon,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { get, put } from "../api/client";
import { AdminDashboardData, HMPDashboardData, AuditEntry, Vacature } from "../types";
import KPICard from "../components/KPICard";
import VacatureListModal from "../components/VacatureListModal";
import VacatureModal from "../components/VacatureModal";
import InlineVacatureReview from "../components/InlineVacatureReview";
import { StatusPill, PriorityPill, BemiddelingPill } from "./Vacatures";
import { Plus, CheckCircle2 } from "lucide-react";
import { post } from "../api/client";
import { Tab, CreateVacatureRequest, UpdateVacatureRequest } from "../types";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  CellValueChangedEvent,
  ValueFormatterParams,
} from "ag-grid-community";

const WAVES_COLORS = ["#65ADDD", "#1B2A4A", "#5CA0CD", "#E0EFF8", "#3D7CB5", "#243558"];

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-8 h-8 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-sm">
      <strong>Fout bij laden:</strong> {message}
    </div>
  );
}

function actionLabel(action: string): string {
  const map: Record<string, string> = {
    create: "Aangemaakt",
    update: "Bijgewerkt",
    delete: "Verwijderd",
    status_change: "Status gewijzigd",
    login: "Ingelogd",
  };
  return map[action] ?? action;
}

function actionBadge(action: string) {
  const styles: Record<string, string> = {
    create: "bg-green-100 text-green-700",
    update: "bg-blue-100 text-blue-700",
    delete: "bg-red-100 text-red-600",
    status_change: "bg-yellow-100 text-yellow-700",
    login: "bg-slate-100 text-slate-600",
  };
  return (
    <span className={`pill ${styles[action] ?? "bg-slate-100 text-slate-600"}`}>
      {actionLabel(action)}
    </span>
  );
}

// ── Clickable KPI Card wrapper ────────────────────────────────────────────────

interface ClickableKPICardProps {
  children: React.ReactNode;
  onClick: () => void;
}

function ClickableKPICard({ children, onClick }: ClickableKPICardProps) {
  return (
    <div
      onClick={onClick}
      className="cursor-pointer group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-waves-blue rounded-2xl"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      style={{ transition: "transform 0.12s ease" }}
      onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)")}
      onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.transform = "translateY(0)")}
    >
      {children}
    </div>
  );
}

// ── Admin Dashboard ────────────────────────────────────────────────────────────
function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState<string | null>(null);

  // KAM filter state
  const [selectedKamId, setSelectedKamId] = useState<number | null>(null);
  const [kams, setKams] = useState<{ id: number; name: string; email: string }[]>([]);

  // Partners state for bar chart click
  const [partners, setPartners] = useState<{ id: number; name: string }[]>([]);

  // Chart modal state
  const [chartModal, setChartModal] = useState<{ title: string; params: string } | null>(null);

  // Fetch KAMs and partners on mount
  useEffect(() => {
    get<{ id: number; name: string; email: string }[]>("/users/kams")
      .then(setKams)
      .catch(() => {});
    get<{ id: number; name: string }[]>("/partners")
      .then(setPartners)
      .catch(() => {});
  }, []);

  // Fetch dashboard data when selectedKamId changes
  useEffect(() => {
    setLoading(true);
    const url = "/dashboard/admin" + (selectedKamId ? "?kam_id=" + selectedKamId : "");
    get<AdminDashboardData>(url)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedKamId]);

  const getPartnerIdByName = (name: string) =>
    partners.find((p) => p.name === name)?.id;

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  const pieData = [
    { name: "Geen", value: data.totaal_vacatures - data.in_bemiddeling - data.geplaatst },
    { name: "In Bemiddeling", value: data.in_bemiddeling },
    { name: "Geplaatst", value: data.geplaatst },
  ];

  return (
    <div className="space-y-6">
      {/* KAM filter dropdown — stacked on mobile, inline on desktop */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
        <span className="text-sm font-medium text-slate-600">KAM weergave:</span>
        <select
          value={selectedKamId ?? ""}
          onChange={(e) =>
            setSelectedKamId(e.target.value ? Number(e.target.value) : null)
          }
          className="select-field w-full md:w-auto"
        >
          <option value="">Alle KAMs</option>
          {kams.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </select>
        {selectedKamId && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-waves-blue-light text-waves-blue w-fit">
            Gefilterd op {kams.find((k) => k.id === selectedKamId)?.name}
          </span>
        )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
        <ClickableKPICard onClick={() => setOpenModal("totaal")}>
          <KPICard icon={Briefcase} label="Totaal Vacatures" value={data.totaal_vacatures} color="#65ADDD" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("open")}>
          <KPICard icon={CheckCircle} label="Open" value={data.open_vacatures} color="#22C55E" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("gesloten")}>
          <KPICard icon={XCircle} label="Gesloten" value={data.gesloten_vacatures} color="#94A3B8" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("hoog")}>
          <KPICard icon={AlertTriangle} label="Hoge Prioriteit" value={data.hoge_prioriteit} color="#EF4444" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("in_bemiddeling")}>
          <KPICard icon={Users} label="In Bemiddeling" value={data.in_bemiddeling} color="#65ADDD" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("geplaatst")}>
          <KPICard icon={TrendingUp} label="Geplaatst" value={data.geplaatst} color="#10B981" />
        </ClickableKPICard>
      </div>

      {/* Charts row — stacked on mobile, side-by-side on desktop */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Bar chart */}
        <div className="card p-4 md:p-6 flex-1 min-w-0">
          <h3 className="font-semibold text-slate-700 mb-4">Vacatures per Partner</h3>
          <div className="overflow-x-auto">
            <div style={{ minWidth: 300 }}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.per_partner} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis
                    dataKey="partner_name"
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #E2E8F0",
                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.08)",
                      fontSize: 13,
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                    iconType="circle"
                  />
                  <Bar
                    dataKey="open"
                    name="Open"
                    fill="#65ADDD"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(data: any) => {
                      const partnerId = getPartnerIdByName(data.partner_name);
                      setChartModal({
                        title: `Open vacatures — ${data.partner_name}`,
                        params: `status=open${partnerId ? "&partner_id=" + partnerId : ""}`,
                      });
                    }}
                  />
                  <Bar
                    dataKey="gesloten"
                    name="Gesloten"
                    fill="#CBD5E1"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(data: any) => {
                      const partnerId = getPartnerIdByName(data.partner_name);
                      setChartModal({
                        title: `Gesloten vacatures — ${data.partner_name}`,
                        params: `status=gesloten${partnerId ? "&partner_id=" + partnerId : ""}`,
                      });
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Pie chart */}
        <div className="card p-4 md:p-6 md:w-72 flex-shrink-0">
          <h3 className="font-semibold text-slate-700 mb-4">Bemiddelingsstatus</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={3}
                dataKey="value"
                cursor="pointer"
                onClick={(_: any, index: number) => {
                  const pieFilters = [
                    "",
                    "bemiddeling_status=in_bemiddeling",
                    "bemiddeling_status=geplaatst",
                  ];
                  const pieTitles = [
                    "Vacatures zonder bemiddeling",
                    "In Bemiddeling",
                    "Geplaatst",
                  ];
                  if (pieFilters[index]) {
                    setChartModal({
                      title: pieTitles[index],
                      params: pieFilters[index],
                    });
                  }
                }}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={WAVES_COLORS[i]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #E2E8F0",
                  fontSize: 13,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-3">
            {pieData.map((item, i) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: WAVES_COLORS[i] }}
                  />
                  <span className="text-slate-600">{item.name}</span>
                </div>
                <span className="font-semibold text-slate-700">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="card p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-700">Recente Activiteit</h3>
          <button
            onClick={() => navigate("/audit-trail")}
            className="flex items-center gap-1 text-sm text-waves-blue hover:text-waves-blue-hover font-medium transition-colors"
          >
            Bekijk alles
            <ChevronRight size={14} />
          </button>
        </div>
        {data.recent_activity.length === 0 ? (
          <p className="text-slate-400 text-sm">Geen recente activiteit.</p>
        ) : (
          <div className="space-y-1">
            {data.recent_activity.map((entry) => (
              <div
                key={entry.id}
                className="flex flex-wrap md:flex-nowrap items-start md:items-center gap-2 md:gap-4 py-2.5 border-b border-slate-50 last:border-0 text-sm"
              >
                <span className="text-slate-400 text-xs w-full md:w-32 md:flex-shrink-0">
                  {new Date(entry.created_at).toLocaleString("nl-NL", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                {actionBadge(entry.action)}
                <span className="text-slate-700 font-medium truncate">
                  {entry.vacature_functie ?? "—"}
                </span>
                <span className="text-slate-400 truncate hidden md:block">{entry.partner_name ?? "—"}</span>
                <span className="md:ml-auto text-slate-500 text-xs truncate">{entry.user_name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KPI Modals */}
      {openModal === "totaal" && (
        <VacatureListModal
          title="Alle Vacatures"
          filterParams=""
          myHmps={false}
          kamId={selectedKamId ?? undefined}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "open" && (
        <VacatureListModal
          title="Open Vacatures"
          filterParams="status=open"
          myHmps={false}
          kamId={selectedKamId ?? undefined}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "gesloten" && (
        <VacatureListModal
          title="Gesloten Vacatures"
          filterParams="status=gesloten"
          myHmps={false}
          kamId={selectedKamId ?? undefined}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "hoog" && (
        <VacatureListModal
          title="Hoge Prioriteit"
          filterParams="status=open&priority=hoog"
          myHmps={false}
          kamId={selectedKamId ?? undefined}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "in_bemiddeling" && (
        <VacatureListModal
          title="In Bemiddeling"
          filterParams="bemiddeling_status=in_bemiddeling"
          myHmps={false}
          kamId={selectedKamId ?? undefined}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "geplaatst" && (
        <VacatureListModal
          title="Geplaatst"
          filterParams="bemiddeling_status=geplaatst"
          myHmps={false}
          kamId={selectedKamId ?? undefined}
          onClose={() => setOpenModal(null)}
        />
      )}

      {/* Chart click modal */}
      {chartModal && (
        <VacatureListModal
          title={chartModal.title}
          filterParams={chartModal.params}
          myHmps={false}
          kamId={selectedKamId ?? undefined}
          onClose={() => setChartModal(null)}
        />
      )}
    </div>
  );
}

// ── KAM Dashboard ────────────────────────────────────────────────────────────
function KAMDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [activeVacatures, setActiveVacatures] = useState<Vacature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [myHmps, setMyHmps] = useState(false);
  const [openModal, setOpenModal] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const myHmpsParam = myHmps ? "?my_hmps=true" : "";
    const vacaturesParam = myHmps
      ? "/vacatures?bemiddeling_status=in_bemiddeling&my_hmps=true"
      : "/vacatures?bemiddeling_status=in_bemiddeling";

    Promise.all([
      get<AdminDashboardData>(`/dashboard/kam${myHmpsParam}`),
      get<Vacature[]>(vacaturesParam),
    ])
      .then(([dash, vacs]) => {
        setData(dash);
        setActiveVacatures(vacs);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [myHmps]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Mijn HMPs toggle — full width on mobile */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
        <span className="text-sm font-medium text-slate-600">Weergave:</span>
        <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1 w-full md:w-auto">
          <button
            onClick={() => setMyHmps(false)}
            className={`flex-1 md:flex-none px-3 py-2 md:py-1.5 rounded-md text-sm font-medium transition-all duration-150 min-h-[44px] md:min-h-0 ${
              !myHmps
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Alle HMPs
          </button>
          <button
            onClick={() => setMyHmps(true)}
            className={`flex-1 md:flex-none px-3 py-2 md:py-1.5 rounded-md text-sm font-medium transition-all duration-150 min-h-[44px] md:min-h-0 ${
              myHmps
                ? "bg-white text-slate-800 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Mijn HMPs
          </button>
        </div>
        {myHmps && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-waves-blue-light text-waves-blue w-fit">
            Gefilterd op jouw HMPs
          </span>
        )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <ClickableKPICard onClick={() => setOpenModal("open")}>
          <KPICard icon={Briefcase} label="Open Vacatures" value={data.open_vacatures} color="#65ADDD" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("hoog")}>
          <KPICard icon={AlertTriangle} label="Hoge Prioriteit" value={data.hoge_prioriteit} color="#EF4444" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("in_bemiddeling")}>
          <KPICard icon={Users} label="In Bemiddeling" value={data.in_bemiddeling} color="#65ADDD" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("geplaatst")}>
          <KPICard icon={TrendingUp} label="Geplaatst" value={data.geplaatst} color="#10B981" />
        </ClickableKPICard>
      </div>

      {/* Active vacatures table */}
      <div className="card p-4 md:p-6">
        <h3 className="font-semibold text-slate-700 mb-4">Actieve Bemiddelingen</h3>
        {activeVacatures.length === 0 ? (
          <p className="text-slate-400 text-sm">Geen actieve bemiddelingen.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Functie</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Locatie</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Bijgewerkt</th>
                </tr>
              </thead>
              <tbody>
                {activeVacatures.map((v) => (
                  <tr key={v.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 px-3 font-medium text-slate-800">{v.functie}</td>
                    <td className="py-3 px-3 text-slate-500 hidden sm:table-cell">{v.locatie ?? "—"}</td>
                    <td className="py-3 px-3">
                      <span className="pill bg-waves-blue-light text-waves-blue">
                        In Bemiddeling
                      </span>
                    </td>
                    <td className="py-3 px-3 text-slate-400 text-xs hidden sm:table-cell">
                      {new Date(v.updated_at).toLocaleDateString("nl-NL")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div className="card p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-700">Recente Activiteit</h3>
          <button
            onClick={() => navigate("/audit-trail")}
            className="flex items-center gap-1 text-sm text-waves-blue hover:text-waves-blue-hover font-medium transition-colors"
          >
            Bekijk alles
            <ChevronRight size={14} />
          </button>
        </div>
        {!data.recent_activity || data.recent_activity.length === 0 ? (
          <p className="text-slate-400 text-sm">Geen recente activiteit.</p>
        ) : (
          <div className="space-y-1">
            {data.recent_activity.slice(0, 5).map((entry) => (
              <div
                key={entry.id}
                className="flex flex-wrap md:flex-nowrap items-start md:items-center gap-2 md:gap-4 py-2.5 border-b border-slate-50 last:border-0 text-sm"
              >
                <span className="text-slate-400 text-xs w-full md:w-32 md:flex-shrink-0">
                  {new Date(entry.created_at).toLocaleString("nl-NL", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                {actionBadge(entry.action)}
                <span className="text-slate-700 font-medium truncate">
                  {entry.vacature_functie ?? "—"}
                </span>
                <span className="text-slate-400 truncate hidden md:block">{entry.partner_name ?? "—"}</span>
                <span className="md:ml-auto text-slate-500 text-xs truncate">{entry.user_name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KPI Modals */}
      {openModal === "open" && (
        <VacatureListModal
          title="Open Vacatures"
          filterParams="status=open"
          myHmps={myHmps}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "hoog" && (
        <VacatureListModal
          title="Hoge Prioriteit"
          filterParams="status=open&priority=hoog"
          myHmps={myHmps}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "in_bemiddeling" && (
        <VacatureListModal
          title="In Bemiddeling"
          filterParams="bemiddeling_status=in_bemiddeling"
          myHmps={myHmps}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "geplaatst" && (
        <VacatureListModal
          title="Geplaatst"
          filterParams="bemiddeling_status=geplaatst"
          myHmps={myHmps}
          onClose={() => setOpenModal(null)}
        />
      )}
    </div>
  );
}

// ── HMP Dashboard ──────────────────────────────────────────────────────────────
function HMPDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<HMPDashboardData | null>(null);
  const [vestigingData, setVestigingData] = useState<{ name: string; open: number }[]>([]);
  const [openVacatures, setOpenVacatures] = useState<Vacature[]>([]);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [chartModal, setChartModal] = useState<{ title: string; params: string } | null>(null);
  const [showVacatureModal, setShowVacatureModal] = useState(false);
  const [editingVacature, setEditingVacature] = useState<Vacature | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const gridRef = useRef<AgGridReact>(null);
  const reviewSectionRef = useRef<HTMLDivElement>(null);

  // Welcome banner: shown when ?welkom=1 is in the URL (set by onboarding magic link).
  // Once dismissed, we store a flag in localStorage so it doesn't reappear.
  const welcomeDismissedKey = user ? `waves:welkom:dismissed:${user.id}` : null;
  const [showWelcome, setShowWelcome] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const isWelkom = new URLSearchParams(window.location.search).get("welkom") === "1";
    if (!isWelkom) return false;
    if (!welcomeDismissedKey) return false;
    return localStorage.getItem(welcomeDismissedKey) !== "1";
  });

  function dismissWelcome() {
    if (welcomeDismissedKey) {
      try {
        localStorage.setItem(welcomeDismissedKey, "1");
      } catch {
        // localStorage unavailable — ignore
      }
    }
    setShowWelcome(false);
    // Strip the welkom param from the URL
    const next = new URLSearchParams(searchParams);
    next.delete("welkom");
    setSearchParams(next, { replace: true });
  }

  // Handle URL query params: ?focus=hoog and ?review=open
  useEffect(() => {
    if (!data) return;

    const focus = searchParams.get("focus");
    const review = searchParams.get("review");

    if (focus === "hoog") {
      setOpenModal("hoog");
      // Clear the param to avoid re-triggering
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }

    if (review === "open") {
      // Scroll to the inline review section if it exists
      if (reviewSectionRef.current) {
        reviewSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      // Clear the param
      const next = new URLSearchParams(searchParams);
      next.delete("review");
      setSearchParams(next, { replace: true });
    }
  }, [data, searchParams, setSearchParams]);

  useEffect(() => {
    const partnerId = user?.partner_id;
    if (!partnerId) {
      setLoading(false);
      return;
    }

    Promise.all([
      get<HMPDashboardData>(`/dashboard/hmp/${partnerId}`),
      get<Vacature[]>(`/vacatures?partner_id=${partnerId}`),
      get<Tab[]>(`/tabs?partner_id=${partnerId}`),
    ])
      .then(([dash, vacs, tabList]) => {
        setData(dash);
        setTabs(tabList);
        // Filter open vacatures and sort: hoge prio first, then by updated_at desc
        const open = vacs
          .filter((v) => v.status === "open")
          .sort((a, b) => {
            const aHigh = a.priority === "hoog" ? 1 : 0;
            const bHigh = b.priority === "hoog" ? 1 : 0;
            if (aHigh !== bHigh) return bHigh - aHigh;
            return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
          });
        setOpenVacatures(open);
        // Group by locatie for bar chart
        const locMap: Record<string, number> = {};
        open.forEach((v) => {
          const loc = v.locatie ?? "Onbekend";
          locMap[loc] = (locMap[loc] ?? 0) + 1;
        });
        setVestigingData(
          Object.entries(locMap).map(([name, openCount]) => ({ name, open: openCount }))
        );
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [user, refreshCounter]);

  async function handleSubmitVacature(payload: CreateVacatureRequest | UpdateVacatureRequest) {
    if (editingVacature) {
      await fetch(`/api/vacatures/${editingVacature.id}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } else {
      await post("/vacatures", payload);
    }
    setShowVacatureModal(false);
    setEditingVacature(null);
    setRefreshCounter((c) => c + 1);
  }

  const partnerId = user?.partner_id;

  // AG Grid column definitions for open vacatures (must be before any early return
  // to keep hook ordering stable)
  const colDefs = useMemo<ColDef<Vacature>[]>(
    () => [
      {
        field: "functie",
        headerName: "Functie",
        minWidth: 160,
        maxWidth: 320,
        flex: 2,
        editable: true,
        cellStyle: { fontWeight: 500 },
      },
      {
        field: "locatie",
        headerName: "Locatie",
        minWidth: 100,
        maxWidth: 200,
        flex: 1,
        editable: true,
        valueFormatter: (p: ValueFormatterParams) => p.value ?? "—",
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 100,
        maxWidth: 140,
        width: 120,
        editable: true,
        cellRenderer: (p: { value: string }) => <StatusPill value={p.value} />,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ["open", "gesloten", "on_hold"] },
      },
      {
        field: "priority",
        headerName: "Prioriteit",
        minWidth: 100,
        maxWidth: 140,
        width: 120,
        editable: true,
        cellRenderer: (p: { value: string }) => <PriorityPill value={p.value} />,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ["hoog", "normaal", "laag"] },
      },
      {
        field: "contactpersoon",
        headerName: "Contactpersoon",
        minWidth: 120,
        maxWidth: 250,
        flex: 1,
        editable: true,
        valueFormatter: (p: ValueFormatterParams) => p.value ?? "—",
      },
      {
        field: "aantal_posities",
        headerName: "Aantal",
        minWidth: 70,
        maxWidth: 100,
        width: 90,
        editable: true,
        type: "numericColumn",
      },
      {
        field: "bemiddeling_status",
        headerName: "Bemiddeling",
        minWidth: 130,
        maxWidth: 180,
        width: 150,
        editable: true,
        cellRenderer: (p: { value: string }) => <BemiddelingPill value={p.value} />,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["geen", "in_bemiddeling", "shortlisted", "geplaatst"],
        },
      },
      {
        field: "updated_at",
        headerName: "Laatste Update",
        minWidth: 120,
        maxWidth: 160,
        width: 140,
        editable: false,
        valueFormatter: (p: ValueFormatterParams) =>
          p.value
            ? new Date(p.value).toLocaleDateString("nl-NL", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "—",
        sort: "desc",
      },
    ],
    []
  );

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <p className="text-slate-500">Geen data beschikbaar.</p>;

  const stale = data.stale_vacatures ?? [];
  const firstname = user?.name?.split(" ")[0] ?? "";

  async function handleGridCellValueChanged(event: CellValueChangedEvent<Vacature>) {
    if (!event.data) return;
    const id = event.data.id;
    const field = event.colDef.field as keyof Vacature;
    if (!field) return;
    try {
      await put<Vacature>(`/vacatures/${id}`, { [field]: event.newValue });
      setRefreshCounter((c) => c + 1);
    } catch (err) {
      console.error("Update mislukt:", err);
      event.node.setDataValue(field, event.oldValue);
    }
  }

  return (
    <div className="space-y-6">
      {/* Welkomstbanner — alleen bij eerste login via onboarding magic link */}
      {showWelcome && (
        <div
          className="relative rounded-xl p-4 md:p-5 border"
          style={{
            background: "linear-gradient(135deg, #65ADDD15 0%, #65ADDD05 100%)",
            borderColor: "#65ADDD40",
          }}
        >
          <button
            onClick={dismissWelcome}
            className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-white/60 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
            aria-label="Sluiten"
          >
            <XIcon size={16} />
          </button>
          <div className="flex items-start gap-3 pr-8">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#65ADDD" }}
            >
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-base md:text-lg">
                Welkom{firstname ? `, ${firstname}` : ""}
              </h3>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Hier zie je je openstaande vacatures en wat je aandacht nodig heeft.
                Klik op een rij om een vacature te reviewen of te bewerken — zo houden we samen alles up-to-date.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Welcome header met actieknop */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-900">
            Hoi {firstname} 👋
          </h2>
          <p className="text-sm text-slate-500 mt-1">Wat wil je vandaag doen?</p>
        </div>
        <button
          onClick={() => { setEditingVacature(null); setShowVacatureModal(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white hover:opacity-90 active:scale-[0.98] shadow-sm min-h-[44px] flex-shrink-0"
          style={{ backgroundColor: "#65ADDD" }}
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Nieuwe vacature</span>
          <span className="sm:hidden">Nieuw</span>
        </button>
      </div>

      {/* Inline review */}
      <div ref={reviewSectionRef}>
      {stale.length > 0 ? (
        <InlineVacatureReview
          vacatures={stale}
          onComplete={() => setRefreshCounter((c) => c + 1)}
        />
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5 md:p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 size={28} className="text-white" />
          </div>
          <div>
            <div className="font-bold text-green-800">Alles up-to-date</div>
            <div className="text-sm text-green-700">Geen vacatures te reviewen</div>
          </div>
        </div>
      )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ClickableKPICard onClick={() => setOpenModal("open")}>
          <KPICard icon={Briefcase} label="Open Vacatures" value={data.open_vacatures} color="#65ADDD" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("hoog")}>
          <KPICard icon={AlertTriangle} label="Hoge Prioriteit" value={data.hoge_prioriteit} color="#EF4444" />
        </ClickableKPICard>
        <ClickableKPICard onClick={() => setOpenModal("in_bemiddeling")}>
          <KPICard icon={Users} label="In Bemiddeling" value={data.in_bemiddeling} color="#10B981" />
        </ClickableKPICard>
      </div>

      {/* Bar chart vestigingen */}
      {vestigingData.length > 0 && (
        <div className="card p-4 md:p-6">
          <h3 className="font-semibold text-slate-700 mb-4">Open Vacatures per Vestiging</h3>
          <div className="overflow-x-auto">
            <div style={{ minWidth: 280 }}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={vestigingData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #E2E8F0",
                      fontSize: 13,
                    }}
                  />
                  <Bar
                    dataKey="open"
                    name="Open"
                    fill="#65ADDD"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(barData: any) => {
                      const locName = barData?.name;
                      if (locName && partnerId) {
                        setChartModal({
                          title: `Open vacatures — ${locName}`,
                          params: `status=open&partner_id=${partnerId}&locatie=${encodeURIComponent(locName)}`,
                        });
                      }
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* KPI Modals */}
      {openModal === "open" && (
        <VacatureListModal
          title="Open Vacatures"
          filterParams={`status=open&partner_id=${partnerId}`}
          myHmps={false}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "hoog" && (
        <VacatureListModal
          title="Hoge Prioriteit"
          filterParams={`status=open&priority=hoog&partner_id=${partnerId}`}
          myHmps={false}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "in_bemiddeling" && (
        <VacatureListModal
          title="In Bemiddeling"
          filterParams={`bemiddeling_status=in_bemiddeling&partner_id=${partnerId}`}
          myHmps={false}
          onClose={() => setOpenModal(null)}
        />
      )}
      {chartModal && (
        <VacatureListModal
          title={chartModal.title}
          filterParams={chartModal.params}
          myHmps={false}
          onClose={() => setChartModal(null)}
        />
      )}

      {/* Openstaande vacatures — AG Grid (desktop) / cards (mobile) */}
      <div className="card p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-700">Openstaande Vacatures</h3>
          <span className="text-xs text-slate-400">{openVacatures.length} totaal</span>
        </div>

        {openVacatures.length === 0 ? (
          <p className="text-slate-400 text-sm">Geen openstaande vacatures.</p>
        ) : (
          <>
            {/* AG Grid — desktop */}
            <div className="hidden md:block ag-theme-alpine" style={{ width: "100%" }}>
              <AgGridReact<Vacature>
                ref={gridRef}
                rowData={openVacatures}
                columnDefs={colDefs}
                animateRows={true}
                rowSelection="single"
                onCellValueChanged={handleGridCellValueChanged}
                stopEditingWhenCellsLoseFocus={true}
                singleClickEdit={false}
                domLayout="autoHeight"
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  filter: false,
                }}
                noRowsOverlayComponent={() => (
                  <div className="text-slate-400 text-sm">Geen openstaande vacatures</div>
                )}
              />
            </div>

            {/* Kaartoverzicht — mobiel */}
            <div className="block md:hidden space-y-3">
              {openVacatures.map((v) => (
                <div
                  key={v.id}
                  className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-slate-900 text-sm truncate">{v.functie}</h4>
                      {v.locatie && (
                        <p className="text-xs text-slate-500 mt-0.5">{v.locatie}</p>
                      )}
                    </div>
                    <StatusPill value={v.status} />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <PriorityPill value={v.priority} />
                    <BemiddelingPill value={v.bemiddeling_status} />
                    {v.contactpersoon && (
                      <span className="text-xs text-slate-500">👤 {v.contactpersoon}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400">
                      {new Date(v.updated_at).toLocaleDateString("nl-NL", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </span>
                    <button
                      onClick={() => { setEditingVacature(v); setShowVacatureModal(true); }}
                      className="text-xs text-waves-blue font-medium hover:underline min-h-[44px] flex items-center gap-1"
                    >
                      Bewerken <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Vacature create/edit modal */}
      {showVacatureModal && partnerId && (
        <VacatureModal
          mode={editingVacature ? "edit" : "create"}
          partnerId={partnerId}
          tabs={tabs}
          vacature={editingVacature ?? undefined}
          onSubmit={handleSubmitVacature}
          onClose={() => { setShowVacatureModal(false); setEditingVacature(null); }}
        />
      )}
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();

  if (!user) return null;

  if (user.role === "admin") return <AdminDashboard />;
  if (user.role === "kam") return <KAMDashboard />;
  if (user.role === "partner_admin") return <PartnerAdminDashboard />;
  return <HMPDashboard />;
}
